import { useEffect, type RefObject } from 'react';

/**
 * Runs the "how it works" step/line-fill animation once the process
 * section scrolls into view, then loops it, matching the original
 * prototype's timing.
 */
export function useProcessAnimation(sectionRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const section = sectionRef.current;
    if (!section || !('IntersectionObserver' in window)) return;

    const steps = [1, 2, 3].map((n) =>
      section.querySelector<HTMLElement>(`[data-process-step="${n}"]`),
    );
    const fill = section.querySelector<HTMLElement>('[data-process-fill]');

    let loopTimer: ReturnType<typeof setTimeout>;
    const stepTimers: ReturnType<typeof setTimeout>[] = [];
    let ran = false;

    const lightUp = (i: number) => {
      const el = steps[i];
      if (!el) return;
      el.style.background = '#d07718';
      el.style.color = '#fff';
      el.style.boxShadow = '0 0 16px rgba(208,119,24,.7)';
    };

    const reset = () => {
      steps.forEach((el) => {
        if (!el) return;
        el.style.background = 'var(--bg)';
        el.style.color = '#d07718';
        el.style.boxShadow = 'none';
      });
      if (fill) {
        fill.style.transition = 'none';
        fill.style.width = '0%';
        // Force reflow so the transition re-applies on the next frame.
        void fill.offsetHeight;
        fill.style.transition = 'width 1.1s cubic-bezier(.4,0,.2,1)';
      }
    };

    const cycle = () => {
      reset();
      stepTimers.push(setTimeout(() => lightUp(0), 200));
      stepTimers.push(
        setTimeout(() => {
          if (fill) fill.style.width = '44%';
        }, 900),
      );
      stepTimers.push(setTimeout(() => lightUp(1), 2100));
      stepTimers.push(
        setTimeout(() => {
          if (fill) fill.style.width = '88%';
        }, 2400),
      );
      stepTimers.push(setTimeout(() => lightUp(2), 3600));
      loopTimer = setTimeout(cycle, 3600 + 4000);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !ran) {
            ran = true;
            cycle();
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -20% 0px' },
    );
    observer.observe(section);

    return () => {
      observer.disconnect();
      clearTimeout(loopTimer);
      stepTimers.forEach(clearTimeout);
    };
  }, [sectionRef]);
}
